import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/meedzerl';

export const ContactSection = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    website_url: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          website_url: formData.website_url ? `https://${formData.website_url}` : 'Ikke oppgitt',
          message: formData.message,
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', website_url: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-secondary/30"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left column - Text */}
          <div className="fade-in-up">
            <h2
              data-testid="contact-title"
              className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {t('contactTitle')}
            </h2>
            <p
              data-testid="contact-subtitle"
              className="text-lg text-muted-foreground leading-relaxed mb-8"
            >
              {t('contactSubtitle')}
            </p>

            {/* Contact info */}
            <div className="space-y-4">
              <a
                href="mailto:kontakt@nordenswebstudio.com"
                data-testid="contact-email"
                className="block text-foreground hover:text-primary transition-colors"
              >
                kontakt@nordenswebstudio.com
              </a>
            </div>
          </div>

          {/* Right column - Form */}
          <div className="fade-in-up stagger-1">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="glass-card p-8 md:p-10 rounded-2xl space-y-6"
            >
              {/* Name field */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  {t('nameLabel')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-name-input"
                  placeholder={t('namePlaceholder')}
                  className="w-full px-4 py-3 rounded-xl bg-background/50 border border-border focus:border-primary/50 focus:ring-1 focus:ring-primary/50 text-foreground placeholder:text-muted-foreground transition-all"
                />
              </div>

              {/* Email field */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  {t('emailLabel')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="contact-email-input"
                  placeholder={t('emailPlaceholder')}
                  className="w-full px-4 py-3 rounded-xl bg-background/50 border border-border focus:border-primary/50 focus:ring-1 focus:ring-primary/50 text-foreground placeholder:text-muted-foreground transition-all"
                />
              </div>

              {/* Website URL field */}
              <div>
                <label
                  htmlFor="website_url"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  {t('websiteLabel')}
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-4 py-3 rounded-l-xl bg-background/70 border border-r-0 border-border text-muted-foreground text-sm">
                    https://
                  </span>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    data-testid="contact-website-input"
                    placeholder={language === 'no' ? 'dinside.no' : 'yoursite.com'}
                    className="w-full px-4 py-3 rounded-r-xl bg-background/50 border border-border focus:border-primary/50 focus:ring-1 focus:ring-primary/50 text-foreground placeholder:text-muted-foreground transition-all"
                  />
                </div>
              </div>

              {/* Message field */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  {t('messageLabel')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  data-testid="contact-message-input"
                  placeholder={t('messagePlaceholder')}
                  className="w-full px-4 py-3 rounded-xl bg-background/50 border border-border focus:border-primary/50 focus:ring-1 focus:ring-primary/50 text-foreground placeholder:text-muted-foreground transition-all resize-none"
                />
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit-button"
                className="w-full px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold text-lg btn-glow transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                    {t('submitting')}
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    {t('submitButton')}
                  </>
                )}
              </button>

              {/* Status messages */}
              {submitStatus === 'success' && (
                <div
                  data-testid="contact-success-message"
                  className="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/30 text-green-400"
                >
                  <CheckCircle size={20} />
                  <span>
                    {language === 'no' 
                      ? 'Takk for din henvendelse! Vi kontakter deg snart.' 
                      : 'Thank you for your message! We will contact you soon.'}
                  </span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div
                  data-testid="contact-error-message"
                  className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400"
                >
                  <AlertCircle size={20} />
                  <span>
                    {language === 'no'
                      ? 'Noe gikk galt. Vennligst prøv igjen.'
                      : 'Something went wrong. Please try again.'}
                  </span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
